import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
} from "@sportscanner/ui";
import { createAccountSchema } from "@sportscanner/schemas";
import AuthLayout from "@/components/auth-layout";
import { apiPost, apiGet, getApiErrorCode } from "@/lib/api";

const PASSWORD_REGEX_UPPER = /[A-Z]/;
const PASSWORD_REGEX_NUMBER = /[0-9]/;

function isPasswordValid(pw: string): boolean {
  return (
    pw.length >= 8 &&
    PASSWORD_REGEX_UPPER.test(pw) &&
    PASSWORD_REGEX_NUMBER.test(pw)
  );
}

interface TokenData {
  email: string;
  role: string;
  clubName: string | null;
}

export default function CreateAccountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(true);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const passwordValid = isPasswordValid(password);
  const passwordMismatch =
    confirmPassword.length > 0 && password !== confirmPassword;
  const passwordWeak = password.length > 0 && !passwordValid;

  const isDisabled =
    firstName.trim().length < 2 ||
    lastName.trim().length < 2 ||
    !passwordValid ||
    passwordMismatch ||
    isSubmitting;

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenError("TOKEN_INVALID");
      setTokenLoading(false);
      return;
    }

    apiGet<TokenData>(`/api/admin/invite/${token}`)
      .then((data) => {
        setTokenData(data);
        setTokenLoading(false);
      })
      .catch((err) => {
        setTokenError(getApiErrorCode(err));
        setTokenLoading(false);
      });
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isDisabled) return;

    // Client-side guard against the same schema the API enforces.
    const parsed = createAccountSchema.safeParse({
      token,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      password,
      confirmPassword,
    });
    if (!parsed.success) {
      setSubmitError(parsed.error.issues[0]?.message ?? "VALIDATION_FAILED");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await apiPost("/api/admin/create-account", parsed.data);

      toast.success(t("auth.createAccount.successToast"));
      navigate("/login");
    } catch (err) {
      setSubmitError(getApiErrorCode(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  // Loading token
  if (tokenLoading) {
    return (
      <AuthLayout>
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AuthLayout>
    );
  }

  // Invalid / expired token
  if (tokenError) {
    return (
      <AuthLayout>
        <div className="space-y-6">
          <div className="space-y-1">
            <h1 className="font-heading text-2xl font-bold text-neutral-800">
              {t("auth.createAccount.heading")}
            </h1>
          </div>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t(`errors.api.${tokenError}`)}</AlertDescription>
          </Alert>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Heading */}
        <div className="space-y-1">
          <h1 className="font-heading text-2xl font-bold text-neutral-800">
            {t("auth.createAccount.heading")}
          </h1>
          <p className="text-sm text-neutral-500">
            {t("auth.createAccount.description")}
          </p>
          {tokenData?.clubName && (
            <p className="text-sm font-medium text-primary-600 mt-1">
              {tokenData.clubName}
            </p>
          )}
        </div>

        {/* Submit error */}
        {submitError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t(`errors.api.${submitError}`)}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          {/* Email — disabled, pre-filled from token */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-neutral-600">
              {t("auth.createAccount.emailLabel")}
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              value={tokenData?.email || ""}
              readOnly
              className="rounded-lg border-neutral-200 bg-neutral-50 text-neutral-500 cursor-not-allowed"
            />
          </div>

          {/* First name */}
          <div className="space-y-2">
            <Label htmlFor="first-name" className="text-sm text-neutral-600">
              {t("auth.createAccount.firstNameLabel")}{" "}
              <span className="text-red-500">*</span>
            </Label>
            <Input
              id="first-name"
              type="text"
              autoComplete="given-name"
              placeholder={t("auth.createAccount.firstNamePlaceholder")}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={isSubmitting}
              className="rounded-lg border-neutral-200 focus:border-primary-500"
            />
          </div>

          {/* Last name */}
          <div className="space-y-2">
            <Label htmlFor="last-name" className="text-sm text-neutral-600">
              {t("auth.createAccount.lastNameLabel")}{" "}
              <span className="text-red-500">*</span>
            </Label>
            <Input
              id="last-name"
              type="text"
              autoComplete="family-name"
              placeholder={t("auth.createAccount.lastNamePlaceholder")}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={isSubmitting}
              className="rounded-lg border-neutral-200 focus:border-primary-500"
            />
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm text-neutral-600">
              {t("auth.createAccount.passwordLabel")}{" "}
              <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("auth.createAccount.passwordPlaceholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                className={`rounded-lg border-neutral-200 focus:border-primary-500 pr-10 ${passwordWeak ? "border-red-400" : ""}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors cursor-pointer"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {passwordWeak && (
              <p className="text-xs text-red-500">
                {t("auth.createAccount.passwordRules")}
              </p>
            )}
          </div>

          {/* Confirm password */}
          <div className="space-y-2">
            <Label
              htmlFor="confirm-password"
              className="text-sm text-neutral-600"
            >
              {t("auth.createAccount.confirmPasswordLabel")}{" "}
              <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirm ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("auth.createAccount.confirmPasswordPlaceholder")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSubmitting}
                className={`rounded-lg border-neutral-200 focus:border-primary-500 pr-10 ${passwordMismatch ? "border-red-400" : ""}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors cursor-pointer"
                tabIndex={-1}
              >
                {showConfirm ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {passwordMismatch && (
              <p className="text-xs text-red-500">
                {t("auth.createAccount.passwordMismatch")}
              </p>
            )}
          </div>
        </div>

        <Button
          type="submit"
          disabled={isDisabled}
          className="w-full py-3 h-auto bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t("auth.createAccount.submitting")}
            </>
          ) : (
            t("auth.createAccount.submit")
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}
